import type {
  Activity, ActivityActor, ActivityChange, ActivityDetail, ActivityEntry, ActivityEvent,
} from '@/types/activity';
import type { TaskStatus } from '@/types/task';
import { formatTaskDate } from '../taskDates';
import { statusLabel } from '../statusMeta';

/**
 * How a task's timeline reads.
 *
 * Written here rather than sent by the server: a line is copy, and the server
 * holding the sentence would mean shipping a deploy to reword one. The server
 * records which fields a save changed and what they held; naming those fields
 * is this file's whole job.
 *
 * Every payload is read defensively. A row written by an earlier deploy is
 * still a row this panel has to render, so a field that is missing or the wrong
 * shape reads as absent rather than throwing.
 */

const DISMISSED: TaskStatus = 'CANCELLED';

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

/** What a field is called when several changed at once and each gets a row. */
const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  priority: 'Priority',
  title: 'Title',
  description: 'Description',
  assignee_user_id: 'Assignee',
  reviewer_user_id: 'Reviewer',
  start_date: 'Start date',
  due_date: 'Due date',
  labels: 'Labels',
  task_notes: 'Notes',
  task_outputs: 'Outputs',
};

/** A payload field as text, or null when it is absent or not text. */
const asText = (value: unknown): string | null =>
  (typeof value === 'string' && value.length > 0 ? value : null);

/** A person in a payload, by the only field a sentence needs. */
const asPersonName = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return asText((value as { display_name?: unknown }).display_name);
};

/** A stored moment as the day the board shows, which is all a date change means. */
const asDay = (value: unknown): string | null => {
  const moment = asText(value);

  return moment ? formatTaskDate(moment) : null;
};

const statusSentence = (from: unknown, to: unknown): string => {
  const landed = asText(to);
  const left = asText(from);

  if (landed === DISMISSED) {
    return 'dismissed this task';
  }

  if (!landed) {
    return 'changed the status';
  }

  if (left === DISMISSED) {
    return `brought this task back to ${statusLabel(landed as TaskStatus)}`;
  }

  return left
    ? `changed status from ${statusLabel(left as TaskStatus)} to ${statusLabel(landed as TaskStatus)}`
    : `set the status to ${statusLabel(landed as TaskStatus)}`;
};

const prioritySentence = (from: unknown, to: unknown): string => {
  const landed = asText(to);
  const named = (value: string) => PRIORITY_LABELS[value] ?? value;

  if (!landed) {
    return 'cleared the priority';
  }

  const left = asText(from);

  return left
    ? `changed priority from ${named(left)} to ${named(landed)}`
    : `set the priority to ${named(landed)}`;
};

const assigneeSentence = (from: unknown, to: unknown): string => {
  const landed = asPersonName(to);

  if (!landed) {
    return 'unassigned this task';
  }

  return asPersonName(from) ? `reassigned this task to ${landed}` : `assigned this task to ${landed}`;
};

const reviewerSentence = (from: unknown, to: unknown): string => {
  const landed = asPersonName(to);

  return landed ? `set the reviewer to ${landed}` : 'removed the reviewer';
};

/** One builder for both dates, which read the same way. */
const dateSentence = (field: string) => (from: unknown, to: unknown): string => {
  const landed = asDay(to);

  if (!landed) {
    return `cleared the ${field}`;
  }

  return asDay(from) ? `moved the ${field} to ${landed}` : `set the ${field} to ${landed}`;
};

const labelsSentence = (from: unknown, to: unknown): string | null => {
  const gained = asText(to);

  if (gained) {
    return `added the label ${gained}`;
  }

  const lost = asText(from);

  return lost ? `removed the label ${lost}` : null;
};

/**
 * One builder for both attachment slots, which read the same way. The field is
 * the relation as it is stored, so a page attached in chat reads here as the
 * list it landed in gaining it.
 */
const attachmentSentence = (kind: string) => (from: unknown, to: unknown): string | null => {
  const gained = asText(to);

  if (gained) {
    return `attached the ${kind} ${gained}`;
  }

  const lost = asText(from);

  return lost ? `removed the ${kind} ${lost}` : null;
};

/**
 * Field to sentence.
 *
 * A map rather than a switch so a field this build has never heard of falls out
 * as "nothing to say" on its own. An entity says which fields it records, but
 * it says so on the server, so a field added there reaches a deployed client
 * before the words for it do.
 */
const SENTENCES: Record<string, (from: unknown, to: unknown) => string | null> = {
  status: statusSentence,
  priority: prioritySentence,
  title: () => 'renamed this task',
  description: () => 'updated the description',
  assignee_user_id: assigneeSentence,
  reviewer_user_id: reviewerSentence,
  start_date: dateSentence('start date'),
  due_date: dateSentence('due date'),
  labels: labelsSentence,
  task_notes: attachmentSentence('note'),
  task_outputs: attachmentSentence('output'),
};

/** What a stored row says changed, guarded like its values are. */
const changesIn = (event: Activity): ActivityChange[] => {
  const data = event.data as ActivityEvent | undefined;

  return data?.type === 'updated' && Array.isArray(data.changes) ? data.changes : [];
};

/** The fields of one save that this build knows how to word. */
const spoken = (event: Activity): ActivityDetail[] =>
  changesIn(event)
    .map(({ field, from, to }) => ({
      field,
      label: FIELD_LABELS[field] ?? field,
      sentence: SENTENCES[field]?.(from, to) ?? null,
    }))
    .filter((detail): detail is ActivityDetail => detail.sentence !== null);

/**
 * What one line says, or null when there is nothing to show for it.
 *
 * Null covers the cases the panel treats the same way: an event whose `type`
 * this build does not know — a line whose event never arrived included — and a
 * save whose fields it cannot word. All are skipped rather than rendered
 * half-empty.
 *
 * One change reads as the sentence for it. Several read as a header with the
 * fields under it, because a save that touched four fields is one thing
 * somebody did and repeating their name four times says otherwise.
 */
export const taskActivityEntry = (event: Activity): ActivityEntry | null => {
  if (event.data?.type === 'created') {
    return { type: 'created', field: null, sentence: 'created this task', details: [] };
  }

  const details = spoken(event);

  if (details.length === 0) {
    return null;
  }

  if (details.length === 1) {
    return {
      type: 'updated',
      field: details[0].field,
      sentence: details[0].sentence,
      details: [],
    };
  }

  return { type: 'updated', field: null, sentence: 'updated this task', details };
};

/**
 * Who a line is credited to.
 *
 * The agent is named rather than the person it acted for: someone reading the
 * timeline needs to know a change was not made by hand. The person is still on
 * the row, and is what the fallback uses when a human line has nobody resolved.
 */
export const taskActivityActorName = (actor: ActivityActor): string =>
  (actor.type === 'user' ? actor.user?.display_name ?? 'Someone' : 'Strive');
